<template>
    <div>
        <div
            ref="dropzone"
            :id="id"
            :class="[
                'image-dropzone',
                {'thumbnail-dropzone': if_thumbnail},
                {'has-background': hasImage}
            ]"
            v-bind="getRootProps()"
            :style="dropzoneStyle"
        >
            <input v-bind="getInputProps()" aria-label="Text input">
            <div v-if="!isDragActive" class="dz-message">
                <img src="/img/icons/avid-icons/arrow-circle-up.svg" alt="">
                <div class="dropzone-title">Drag and drop or click to upload</div>
                <div class="dropzone-subtitle" v-if="acceptAllFiles">We recommend using files less than 20MB</div>
                <div class="dropzone-subtitle" v-else>We recommend using high quality image files less than 20MB</div>
            </div>
        </div>
        <button v-if="modelValue && modelValue.trim() && !hideDeleteButton" type="button" class="image-delete-btn" v-show="!disabled"
            :class="if_thumbnail? 'thumbnail-image-delete-btn' : ''" @click.prevent="deleteImage" :disabled="disabled">
            <i class="fa fa-trash"></i>
        </button>
    </div>
</template>

<script>
import { useDropzone } from 'vue3-dropzone';
import { defineComponent, ref, onMounted, computed, watch } from 'vue';

export default defineComponent({
    name: 'ImageDropzone',
    props: {
        modelValue: {
            type: String,
            default: ''
        },
        id: {
            type: String,
            default: 'thumbnail-dropzone'
        },
        disabled: {
            type: Boolean,
            default: false
        },
        if_thumbnail: {
            type: Boolean,
            default: true
        },
        acceptAllFiles: {
            type: Boolean,
            default: false
        },
        emitFileMetadata: {
            type: Boolean,
            default: false
        },
        hideDeleteButton: {
            type: Boolean,
            default: false
        },
        clientUrl: {
            type: String,
            default: null
        },
        maxFiles: {
            type: Number,
            default: 1
        },
        minHeight: {
            type: [String, Number],
            default: null
        }
    },
    emits: ['update:modelValue', 'file-uploaded', 'upload-progress'],
    setup(props, { emit }) {
        const dropzone = ref(null);
        const hasImage = ref(!!props.modelValue);

        const dropzoneStyle = computed(() => {
            const style = {};
            if (hasImage.value) {
                style.backgroundImage = `url('${props.modelValue}')`;
            } else {
                style.backgroundImage = 'none';
            }
            if (props.minHeight != null && props.minHeight !== '') {
                style.minHeight = typeof props.minHeight === 'number'
                    ? `${props.minHeight}px`
                    : String(props.minHeight);
            }
            return style;
        });

        const onUploadSuccess = (publicUrl, file = null) => {
            hasImage.value = true;
            if (props.emitFileMetadata && file) {
                emit('update:modelValue', {
                    url: publicUrl,
                    type: file.type,
                    name: file.name
                });
            } else {
                emit('update:modelValue', publicUrl);
            }
        };

        const uploadFile = async (file, fileIndex, totalFiles) => {
            const requestData = {
                file_name: `${Date.now()}_${file.name}`,
                content_type: file.type,
            };
            if (props.clientUrl) {
                requestData.client_url = props.clientUrl;
            }
            const resp = await window.axios.post('/api/file_upload/get_signed_image_url', requestData);
            if (!resp.data?.upload_url || !resp.data?.public_url) {
                throw new Error('Missing upload URL');
            }
            await window.axios.put(resp.data.upload_url, file, {
                headers: { 'Content-Type': file.type },
                withCredentials: false,
                onUploadProgress: (progressEvent) => {
                    const percent = progressEvent.total
                        ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
                        : 0;
                    emit('upload-progress', {
                        fileIndex,
                        totalFiles,
                        percent,
                        fileName: file.name
                    });
                }
            });
            return resp.data.public_url;
        };

        const dropzoneOptions = computed(() => ({
            url: "#",
            resizeWidth: props.if_thumbnail ? 400 : 512,
            resizeHeight: props.if_thumbnail ? 230 : 512,
            resizeMethod: 'contain',
            acceptedFiles: props.acceptAllFiles ? null : "image/*",
            maxFiles: props.maxFiles,
            dictDefaultMessage: "<img src='/img/icons/avid-icons/arrow-circle-up.svg' alt=''><div class='dropzone-title'>Drag and drop or click to upload</div><div class='dropzone-subtitle'>We recommend using high quality image files less than 20MB</div>",
            noClick:  props.disabled,
            disabled: props.disabled,
            noDrag: props.disabled,
            thumbnailWidth: props.if_thumbnail ? 400 : undefined,
            thumbnailHeight: props.if_thumbnail ? 230 : undefined,
            thumbnailMethod: 'contain',
            autoProcessQueue: false,
            onDrop: async (acceptedFiles) => {
                const files = (acceptedFiles || []).slice(0, props.maxFiles);
                if (files.length === 0) return;
                const totalFiles = files.length;
                try {
                    for (let i = 0; i < files.length; i++) {
                        const file = files[i];
                        const fileIndex = i + 1;
                        emit('upload-progress', {
                            fileIndex,
                            totalFiles,
                            percent: 0,
                            fileName: file.name
                        });
                        try {
                            const publicUrl = await uploadFile(file, fileIndex, totalFiles);
                            if (totalFiles === 1) {
                                onUploadSuccess(publicUrl, file);
                            } else {
                                emit('file-uploaded', publicUrl);
                            }
                        } catch (error) {
                            console.error('Error uploading file:', error);
                        }
                    }
                } finally {
                    emit('upload-progress', null);
                }
            }
        }));

        const { getRootProps, getInputProps, isDragActive, open } = useDropzone(dropzoneOptions.value);

        onMounted(() => {
            if (dropzone.value) {
                dropzone.value.addEventListener('click', open);
            }
        });

        const deleteImage = () => {
            // console.log('deleteImage');
            hasImage.value = false;
            emit('update:modelValue', '');
        };

        // Watch for changes in modelValue
        watch(() => props.modelValue, (newValue) => {
            hasImage.value = !!newValue;
        });

        return {
            dropzone,
            getRootProps,
            getInputProps,
            isDragActive,
            deleteImage,
            hasImage,
            dropzoneStyle
        };
    }
});
</script> 